import { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Modal,
  Row,
  Col,
  Divider,
  Select,
  Input,
  Alert,
  Button,
  Space,
  Typography,
  Skeleton,
} from 'antd';
import FloatingFormItem from '../FloatingFormItem';
import {
  BankAccountInfiniteSelectField,
  CompaniesInfiniteSelectField,
  ContentWrapper,
  DocumentTypesInfiniteSelectField,
  Group,
  InputDate,
  InputDecimal,
  PlanAccountsSelect,
} from '@/components/_common';
import CostCenterInfiniteSelectField from '../CostCenterInfiniteSelectField';
import StatusTag from '@/components/_common/StatusTag';

import {
  PlanAccountDestination,
  PlanAccountHierarchy,
} from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import {
  AccountReceivableDebtor,
  AccountReceivableFormValues,
  AccountReceivableSituation,
  AccountReceivableSituationColor,
  AccountRecivableSituationType,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import {
  useAccountReceivableDetail,
  useAccountReceivableEdit,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import useGetInstallmentRate from '@/hooks/useGetInstallmentRate';
import {
  useDefaultEmptyFieldError,
  useDefaultMaxLengthFieldError,
} from '@/hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import InsurerInputSearch from '../InsurerInputSearch';
import CustomerInputSearch from '../CustomerInputSearch';
import { useMatrixCustomerSettingsDetails } from '@/core/domain/enterprise/MatrixCustomerSettings/MatrixCustomerSettings.hooks';
import AccountReceivableObservations from '@/components/Registry/BillToReceive/ReleaseForm/AccountReceivableObservations';
import moment from 'moment';
import useDefaultMinLengthNegativeFieldError from '@/hooks/useDefaultMinLengthNegativeFieldError';
import {
  useDocumentType,
  useDocumentTypeMutation,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';
import { CheckSquare, HelpCircle } from 'react-feather';
import CustomStatistic from '../CustomStatistic';
import SpaceForButton from '../SpaceForButtons';
import { useBankAccountMutation } from '@/core/domain/enterprise/BankAccounts/BankAccounts.hooks';
import BankSlipDownload from '@/components/Financial/BankSlip/BankSlipDownload';
import BankSlipWhatsApp from '@/components/Financial/BankSlip/BankSlipWhatsApp';

interface Props {
  open: boolean;
  onCancel: () => void;
  selectedItem?: {
    accountReceivableId: number;
    installmentId: number;
  };
}

const BillToReceiveEditModal = ({ open, onCancel, selectedItem }: Props) => {
  const [form] = Form.useForm<AccountReceivableFormValues>();
  const [currentInstallmentIndex, setCurrentInstallmentIndex] = useState(0);
  const [hasUpdateTariff, setUpdateTariff] = useState(false);

  const accountReceivable = useAccountReceivableDetail(
    Number(selectedItem?.accountReceivableId),
  );
  const documentTypeId = Form.useWatch('documentTypeId', form);
  const { data: documentType, isLoading: documentTypeLoading } =
    useDocumentType(documentTypeId);
  const { data: customerMatrixSettings } = useMatrixCustomerSettingsDetails();
  const accountReceivableEdit = useAccountReceivableEdit();
  const bankAccountMutation = useBankAccountMutation();
  const getInstallmentRate = useGetInstallmentRate();
  const documentTypeDetail = useDocumentTypeMutation();

  const accountReceivableData = accountReceivable.data;
  const installmentReceivable =
    accountReceivableData?.installmentReceivables || [];
  const installmentData = installmentReceivable[currentInstallmentIndex];

  const discountIncrease = Form.useWatch('discountIncrease', form) || 0;
  const installmentValue = Form.useWatch('installmentValue', form);
  const amountAdjusted = Form.useWatch('amountAdjusted', form);
  const netValue = Form.useWatch('netValue', form);
  const amountReceivable = Form.useWatch('amountReceivable', form);
  const debtor = Form.useWatch('debtor', form);

  const defaultEmptyFieldError = useDefaultEmptyFieldError();
  const defaultMaxLenghtFieldError = useDefaultMaxLengthFieldError();
  const defaultMinLengthNegativeFieldError =
    useDefaultMinLengthNegativeFieldError();

  const hasWorkorderId = !!accountReceivableData?.workOrderId;
  const hasDirectSaleId = !!accountReceivableData?.directSaleId;
  const isStoneOrder = !!accountReceivableData?.isStoneOrder;
  const enabledValueEdit =
    hasPermissions([Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_EDIT_VALUE]) ||
    true;
  const disabledBySituation =
    installmentData?.situation !== AccountReceivableSituation.open;
  const hasInstallmentReceivableBill =
    installmentData?.hasInstallmentReceivableBill;

  const hasInstallmentsSettled = useMemo(
    () =>
      installmentReceivable.some(
        (item) => item.situation !== AccountReceivableSituation.open,
      ),
    [installmentReceivable],
  );

  const oldPercentage = useMemo(() => {
    return (
      (installmentData?.rateValue || 0) /
      ((installmentData?.installmentValue || 0) +
        (installmentData?.discountIncrease || 0))
    );
  }, [installmentData]);

  const hasDocumentTypeInstallmentSettled = useMemo(
    () =>
      installmentReceivable.some(
        (item) =>
          item.situation === AccountReceivableSituation.settled &&
          (item.documentTypeId === documentTypeId ||
            item.documentTypeId === installmentData?.documentTypeId),
      ),
    [installmentReceivable, documentTypeId, installmentData?.documentTypeId],
  );

  const currentPercentage = useMemo(() => {
    if (documentType?.fixedRate) {
      return (documentType.percentRate || 0) / 100;
    } else {
      const installmentSize = installmentReceivable?.filter(
        ({ documentTypeId }) => documentTypeId === documentTypeId,
      )?.length;

      if (typeof installmentSize === 'number') {
        const rate = documentType?.rates.find(
          (rate) => rate.numberInstallments === installmentSize,
        );
        return (rate?.percentRate || 0) / 100;
      }
      return 0;
    }
  }, [documentType, installmentReceivable, documentTypeId]);

  const hasDiffInstallmentValue = useMemo(() => {
    return (
      hasWorkorderId &&
      installmentValue?.toFixed(2) !==
        installmentData?.installmentValue?.toFixed(2)
    );
  }, [installmentValue, installmentData?.installmentValue, hasWorkorderId]);

  const statusColor = installmentData
    ? AccountReceivableSituationColor[installmentData.situation]
    : 'default';

  const statusLabel = installmentData
    ? AccountRecivableSituationType[installmentData.situation]
    : null;

  useEffect(() => {
    if (selectedItem && accountReceivableData) {
      const initialIndex =
        accountReceivableData.installmentReceivables.findIndex(
          (item) => selectedItem.installmentId === item.id,
        );
      setCurrentInstallmentIndex(initialIndex >= 0 ? initialIndex : 0);
    }
  }, [selectedItem, accountReceivableData]);

  useEffect(() => {
    if (accountReceivableData && typeof currentInstallmentIndex === 'number') {
      const installmentReceivable =
        accountReceivableData.installmentReceivables[currentInstallmentIndex];

      if (installmentReceivable?.bankAccountId) {
        bankAccountMutation.mutateAsync(installmentReceivable.bankAccountId);
      } else {
        bankAccountMutation.reset();
      }

      form.setFieldsValue({
        installmentId: installmentReceivable?.id,
        companyId: accountReceivableData.companyId,
        costCenterId: accountReceivableData.costCenterId,
        authorizationNumber: accountReceivableData.authorizationNumber,
        debtor: accountReceivableData.customerId
          ? AccountReceivableDebtor.Costumer
          : AccountReceivableDebtor.Insurer,
        customerId: accountReceivableData.customerId,
        insuerId: accountReceivableData.insurerId,
        planAccountId: accountReceivableData.planAccountId,
        installmentNumber: installmentReceivable?.installmentNumber,
        bankAccountId: installmentReceivable?.bankAccountId,
        observation: installmentReceivable?.observation,
        dueDate: installmentReceivable?.dueDate
          ? moment(installmentReceivable.dueDate)
          : undefined,
        emissionDate: installmentReceivable?.emissionDate
          ? moment(installmentReceivable.emissionDate)
          : undefined,
        payDay: installmentReceivable?.payDate
          ? moment(installmentReceivable.payDate)
          : undefined,
        description: installmentReceivable?.description,
        documentTypeId: installmentReceivable?.documentTypeId,
        documentNumber: installmentReceivable?.documentNumber,
        rateValue: installmentReceivable?.rateValue,
        installmentValue: installmentReceivable?.installmentValue || 0,
        discountIncrease: installmentReceivable?.discountIncrease || 0,
        netValue:
          (installmentReceivable?.installmentValue || 0) +
          (installmentReceivable?.discountIncrease || 0) -
          (installmentReceivable?.rateValue || 0),
        amountReceivable:
          (installmentReceivable?.installmentValue || 0) +
          (installmentReceivable?.discountIncrease || 0),
        amountAdjusted: installmentReceivable?.amountAdjusted,
      });
    }
  }, [accountReceivableData, currentInstallmentIndex, form]);

  const handleFormValuesChange = async (
    changedValues: Partial<AccountReceivableFormValues>,
    allValues: AccountReceivableFormValues,
  ) => {
    if (changedValues.documentTypeId !== undefined) {
      form.setFieldValue('updateTariff', true);
    }

    form.setFieldValue(
      'amountReceivable',
      (allValues.installmentValue ?? 0) + (allValues.discountIncrease ?? 0),
    );

    if (changedValues.debtor !== undefined) {
      form.setFieldsValue({
        customerId: undefined,
        customerName: undefined,
        insuerId: undefined,
        insurerName: undefined,
      });
    }

    if (changedValues.documentTypeId && setUpdateTariff) {
      setUpdateTariff(false);
    }

    if (
      ['installmentValue', 'discountIncrease', 'documentTypeId'].some(
        (key) => key in changedValues,
      )
    ) {
      const tariff =
        allValues.documentTypeId !== installmentData?.documentTypeId
          ? await getInstallmentRate({
              installmentValue: allValues.installmentValue ?? 0,
              discountIncrease: allValues.discountIncrease ?? 0,
              currentInstallment: currentInstallmentIndex,
              installmentReceivables: installmentReceivable,
              documentTypeId: allValues.documentTypeId,
            })
          : ((allValues.installmentValue || 0) +
              (allValues.discountIncrease || 0)) *
              oldPercentage || 0;

      form.setFieldValue('rateValue', tariff);
      form.setFieldValue(
        'netValue',
        (allValues.installmentValue ?? 0) +
          (allValues.discountIncrease ?? 0) -
          (tariff ?? 0),
      );
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleOk = async () => {
    try {
      if (accountReceivableData?.id && installmentData) {
        const values = await form.validateFields();

        await accountReceivableEdit.mutateAsync({
          ...values,
          currentSituation: installmentData.situation,
          accountReceivableId: accountReceivableData.id,
          installmentId: values.installmentId,
          dueDate: values.dueDate?.format(),
          emissionDate: values.emissionDate?.format(),
          payDay: values.payDay?.format(),
        });

        handleCancel();
      }
    } catch (error) {
      console.error('Error saving account receivable:', error);
    } finally {
      setUpdateTariff(false);
    }
  };

  const handleDocumentTypeChange = async (documentTypeId: number) => {
    const documentDetails = await documentTypeDetail.mutateAsync(
      documentTypeId,
    );
    if (documentDetails?.payOnTime) {
      form.setFieldValue('bankAccountId', documentDetails.bankAccountId);
    }
  };

  const confirmModal = () => {
    Modal.confirm({
      title: 'Deseja realmente atualizar a tarifa?',
      content:
        'Ao confirmar, o percentual da tarifa será atualizado de acordo com a tarifa salva no tipo de documento e não será mais possível reverter esta operação',
      onOk: handleOk,
      okText: 'Atualizar',
    });
  };

  const renderDebtorField = () => {
    if (debtor === AccountReceivableDebtor.Insurer) {
      return (
        <Col span={24} md={12} lg={8}>
          <Form.Item hidden name="insuerId" />
          <FloatingFormItem
            label="Seguradora"
            name="insurerName"
            rules={
              hasPermissions([Authorities.REGISTER_INSURER_VIEW])
                ? [defaultEmptyFieldError]
                : []
            }
          >
            <InsurerInputSearch
              disabled
              fieldNameId="insuerId"
              onInsurerSelect={(insurer) =>
                form.setFieldsValue({
                  insuerId: insurer.id,
                  insurerName: insurer.name,
                })
              }
              buttonSearchDisabled={
                disabledBySituation ||
                hasInstallmentsSettled ||
                hasInstallmentReceivableBill ||
                hasWorkorderId ||
                hasDirectSaleId
              }
            />
          </FloatingFormItem>
        </Col>
      );
    }

    return (
      <Col span={24} md={12} lg={8}>
        <Form.Item hidden name="customerId" />
        <FloatingFormItem
          label="Cliente"
          name="customerName"
          rules={[defaultEmptyFieldError]}
        >
          <CustomerInputSearch
            addonAfter={
              accountReceivableData?.customerCategory?.id &&
              customerMatrixSettings?.showCategoryInAccountReceivable ? (
                <StatusTag
                  color={accountReceivableData.customerCategory.color}
                  label={accountReceivableData.customerCategory.name}
                  title={accountReceivableData.customerCategory.name}
                  style={{ width: 100, display: 'block' }}
                  size="lg"
                />
              ) : undefined
            }
            fieldNameId="customerId"
            disabled
            onCustomerSelect={(customer) =>
              form.setFieldsValue({
                customerId: customer.id,
                customerName: customer.name,
              })
            }
            buttonSearchDisabled={
              disabledBySituation ||
              hasInstallmentsSettled ||
              hasInstallmentReceivableBill ||
              hasWorkorderId ||
              hasDirectSaleId
            }
          />
        </FloatingFormItem>
      </Col>
    );
  };

  const renderTariffUpdateButton = () => {
    if (
      !installmentData ||
      installmentData.documentTypeId !== documentTypeId ||
      installmentData.situation === AccountReceivableSituation.settled ||
      oldPercentage.toFixed(4) === currentPercentage.toFixed(4) ||
      documentTypeLoading ||
      hasInstallmentsSettled ||
      hasInstallmentReceivableBill
    ) {
      return null;
    }

    return (
      <Col span={24} md={12} lg={6}>
        <Group style={{ width: '100%' }} gap={10}>
          <Button
            block
            size="large"
            onClick={async () => {
              const tariff = await getInstallmentRate({
                installmentValue: installmentValue ?? 0,
                discountIncrease: discountIncrease ?? 0,
                currentInstallment: currentInstallmentIndex,
                installmentReceivables: installmentReceivable,
                documentTypeId,
              });

              form.setFieldValue('rateValue', tariff);
              form.setFieldValue(
                'netValue',
                (installmentValue ?? 0) +
                  (discountIncrease ?? 0) -
                  (tariff ?? 0),
              );
              setUpdateTariff(true);
            }}
          >
            Atualizar tarifa
          </Button>
          <HelpCircle
            style={{ cursor: 'pointer' }}
            onClick={() =>
              Modal.info({
                title: (
                  <Space direction="vertical">
                    <Typography.Text>
                      <strong>Atualizar tarifa:</strong> utiliza a taxa atual do
                      tipo de documento.
                    </Typography.Text>
                  </Space>
                ),
              })
            }
          />
        </Group>
      </Col>
    );
  };

  return (
    <Modal
      title="Conta a receber"
      open={open}
      width="90%"
      okText="Salvar"
      onCancel={handleCancel}
      onOk={hasUpdateTariff ? confirmModal : handleOk}
      okButtonProps={{
        loading: accountReceivableEdit.isLoading,
        disabled:
          !hasPermissions([Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_EDIT]) ||
          installmentData?.situation === AccountReceivableSituation.settled ||
          installmentData?.situation === AccountReceivableSituation.canceled,
      }}
      keyboard
      closable
      confirmLoading={accountReceivableEdit.isLoading}
    >
      <Skeleton loading={accountReceivable.isLoading}>
        <Form
          layout="vertical"
          form={form}
          onValuesChange={handleFormValuesChange}
        >
          <ContentWrapper
            style={{
              width: '100%',
              marginBottom: 20,
              padding: '1.5rem 2rem 1.25rem',
            }}
          >
            <Row gutter={[16, 9]}>
              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  label="Empresa"
                  name="companyId"
                  rules={[defaultEmptyFieldError]}
                >
                  <CompaniesInfiniteSelectField
                    disabled={
                      hasInstallmentsSettled || hasInstallmentReceivableBill
                    }
                    queryEnable
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  label="Devedor"
                  name="debtor"
                  rules={[defaultEmptyFieldError]}
                  initialValue={AccountReceivableDebtor.Costumer}
                >
                  <Select
                    allowClear
                    style={{ width: '100%' }}
                    disabled={
                      disabledBySituation ||
                      hasInstallmentsSettled ||
                      hasInstallmentReceivableBill ||
                      hasWorkorderId ||
                      hasDirectSaleId
                    }
                    options={[
                      {
                        value: AccountReceivableDebtor.Costumer,
                        label: 'Cliente',
                      },
                      {
                        value: AccountReceivableDebtor.Insurer,
                        label: 'Seguradora',
                      },
                    ]}
                  />
                </FloatingFormItem>
              </Col>

              {renderDebtorField()}

              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  label="Plano de Contas"
                  name="planAccountId"
                  rules={[defaultEmptyFieldError]}
                >
                  <PlanAccountsSelect
                    disabled={disabledBySituation || hasInstallmentsSettled}
                    queryFilter={{
                      hierarchy: PlanAccountHierarchy.Analitical,
                      destination: PlanAccountDestination.Revenue,
                      active: true,
                    }}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={8}>
                <FloatingFormItem label="Centro de Custo" name="costCenterId">
                  <CostCenterInfiniteSelectField
                    disabled={disabledBySituation || hasInstallmentsSettled}
                  />
                </FloatingFormItem>
              </Col>

              <Col span={24} md={12} lg={8}>
                <FloatingFormItem
                  label="Número de autorização"
                  name="authorizationNumber"
                >
                  <Input disabled />
                </FloatingFormItem>
              </Col>
            </Row>
          </ContentWrapper>

          <Row gutter={[16, 9]}>
            <Col span={24}>
              <FloatingFormItem name="situation">
                <StatusTag color={statusColor} label={statusLabel} />
              </FloatingFormItem>
            </Col>
          </Row>

          <Form.Item hidden name="installmentId" noStyle />
          <Form.Item hidden name="amountReceivable" />

          {hasDocumentTypeInstallmentSettled &&
            installmentData?.documentTypeId !== documentTypeId && (
              <Alert
                style={{ marginBottom: 24 }}
                type="warning"
                message={<strong>Alteração de tarifa</strong>}
                description={
                  <>
                    <p>
                      Esse tipo de documento já póssui parcelas liquidadas,
                      modificando as parcelas será recalculado apenas as
                      parcelas em aberto gerando divergências nas tarífas. Para
                      recalcular todas as parcelas será necessário o estorno das
                      parcelas já liquidadas. Deseja prosseguir?
                    </p>
                    <small>Salve as alterações para ignorar o alerta</small>
                  </>
                }
              />
            )}

          <Row gutter={[16, 9]}>
            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Descrição"
                name="description"
                rules={[defaultEmptyFieldError]}
              >
                <Input
                  disabled={disabledBySituation || hasInstallmentReceivableBill}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Número do Documento"
                name="documentNumber"
                rules={[defaultMaxLenghtFieldError(255)]}
              >
                <Input
                  disabled={disabledBySituation || hasInstallmentReceivableBill}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Tipo de Documento"
                name="documentTypeId"
                rules={[defaultEmptyFieldError]}
              >
                <DocumentTypesInfiniteSelectField
                  onChange={handleDocumentTypeChange}
                  disabled={
                    disabledBySituation ||
                    isStoneOrder ||
                    hasInstallmentReceivableBill
                  }
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Dt. Emissão"
                name="emissionDate"
                initialValue={moment()}
                rules={[defaultEmptyFieldError]}
              >
                <InputDate
                  disabled={disabledBySituation || hasInstallmentReceivableBill}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="Dt. Vencimento"
                name="dueDate"
                rules={[defaultEmptyFieldError]}
              >
                <InputDate
                  disabled={disabledBySituation || hasInstallmentReceivableBill}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem label="Dt. Pagamento" name="payDay">
                <InputDate
                  disabled={disabledBySituation || hasInstallmentReceivableBill}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={12}>
              <FloatingFormItem name="bankAccountId" label="Conta Bancária">
                <BankAccountInfiniteSelectField
                  disabled={
                    disabledBySituation ||
                    hasInstallmentReceivableBill ||
                    !!documentTypeDetail.data?.bankAccountId
                  }
                  queryFilter={{ active: true }}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="(R$) Valor"
                name="installmentValue"
                rules={[
                  defaultEmptyFieldError,
                  {
                    type: 'number',
                    min: 0.01,
                    message: 'Valor deve ser maior que R$ 0,00',
                  },
                ]}
                initialValue={0}
              >
                <InputDecimal
                  min={0.01}
                  disabled={
                    disabledBySituation ||
                    hasInstallmentReceivableBill ||
                    !enabledValueEdit
                  }
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="(R$) Acres/Desc"
                name="discountIncrease"
                initialValue={0}
                rules={[
                  defaultMinLengthNegativeFieldError(installmentValue * -1),
                ]}
              >
                <InputDecimal
                  disabled={disabledBySituation || hasInstallmentReceivableBill}
                />
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12} lg={6}>
              <FloatingFormItem
                label="(R$) Tarifa"
                name="rateValue"
                initialValue={0}
              >
                <InputDecimal disabled />
              </FloatingFormItem>
            </Col>

            {renderTariffUpdateButton()}

            {!!amountAdjusted && (
              <Col span={24} md={12} lg={6}>
                <Group style={{ width: '100%' }} gap={10}>
                  <FloatingFormItem
                    label="Valor ajustado"
                    name="amountAdjusted"
                    initialValue={0}
                  >
                    <InputDecimal disabled />
                  </FloatingFormItem>
                  <HelpCircle
                    style={{ cursor: 'pointer' }}
                    onClick={() =>
                      Modal.info({
                        title: (
                          <Space direction="vertical">
                            <Typography.Text>
                              Campo referente a valores que o cliente pagou de
                              diferença no boleto: Juros, Multa, Descontos, etc.
                            </Typography.Text>
                          </Space>
                        ),
                      })
                    }
                  />
                </Group>
              </Col>
            )}

            <Col span={24} md={12} lg={6} hidden>
              <FloatingFormItem
                label="(R$) Valor Líquido"
                name="netValue"
                initialValue={0}
              >
                <InputDecimal disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24}>
              <Group gap={18}>
                <CustomStatistic
                  title="Total pago pelo cliente"
                  description="totalPagoPeloCliente = valorParcela + descontoAcrescimo"
                  value={amountReceivable}
                  valueStyle={{ color: '#148e28' }}
                />
                <CustomStatistic
                  title="Líquido a receber"
                  description="valorLiquido = (valorParcela + descontoAcrescimo) - tarifas"
                  value={netValue}
                  valueStyle={{ color: '#4bbacf' }}
                />
              </Group>
            </Col>

            {installmentData?.hasInstallmentReceivableBill &&
              !!installmentData?.billId && (
                <Col span={24}>
                  <Group gap={12} align="center">
                    <Group gap={10}>
                      <CheckSquare color="green" />

                      <Typography.Title
                        level={4}
                        style={{
                          margin: 0,
                          whiteSpace: 'nowrap',
                          lineHeight: 1,
                        }}
                      >
                        Boleto Gerado
                      </Typography.Title>
                    </Group>

                    <Divider type="vertical" />

                    <SpaceForButton>
                      <BankSlipDownload
                        bankSlip={{
                          id: installmentData?.billId,
                          ourNumber: installmentData.billOurNumber,
                          printProtocol: installmentData.billPrintProtocol,
                        }}
                        show={!!installmentData?.billId}
                        type="button"
                      />

                      <BankSlipWhatsApp
                        bankSlip={{
                          id: installmentData.billId,
                          printProtocol: installmentData.billPrintProtocol,
                          customerId: accountReceivable?.data?.customerId,
                        }}
                        show={!!installmentData?.billId}
                        type="button"
                      />
                    </SpaceForButton>
                  </Group>
                </Col>
              )}

            {hasDiffInstallmentValue && (
              <Col span={24}>
                <Alert
                  type="warning"
                  description="A alteração do valor pode impactar no resumo financeiro do orçamento relacionado."
                />
              </Col>
            )}

            <Col span={24}>
              <AccountReceivableObservations />
            </Col>
          </Row>
        </Form>
      </Skeleton>
    </Modal>
  );
};

export default BillToReceiveEditModal;
